<template>
  <div>
    <TitlePage :title="'Statistiche partita ' + matchId.slice(-6).toUpperCase()"></TitlePage>

    <v-btn text small class="mb-4" to="/match-stats">
      <v-icon left small>mdi-arrow-left</v-icon>
      Lista partite
    </v-btn>

    <div v-if="loading" class="d-flex justify-center pa-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else>
      <v-card class="msd-score-card mb-6" outlined>
        <v-card-text>
          <div class="msd-score">
            <div>
              <div class="caption text-uppercase font-weight-bold text--secondary">Risultato partita</div>
              <div class="body-2 text--secondary">Ereditato dalla partita e applicato alle statistiche utente</div>
            </div>
            <div class="msd-score__board">
              <div class="msd-score__team msd-score__team--a">
                <span>Squadra A</span>
                <v-text-field
                  v-model="scoreEdit.final_score_a"
                  class="msd-score__input"
                  type="number"
                  hide-details
                  solo
                  flat
                  dense
                  :disabled="!canEditStats"
                />
              </div>
              <div class="msd-score__separator">-</div>
              <div class="msd-score__team msd-score__team--b">
                <span>Squadra B</span>
                <v-text-field
                  v-model="scoreEdit.final_score_b"
                  class="msd-score__input"
                  type="number"
                  hide-details
                  solo
                  flat
                  dense
                  :disabled="!canEditStats"
                />
              </div>
              <v-btn
                color="primary"
                depressed
                class="msd-score__save"
                :loading="savingScore"
                :disabled="!canEditStats || !canSaveScore"
                @click="saveMatchScore"
              >
                <v-icon left small>mdi-content-save</v-icon>
                Salva risultato
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- MVP votes -->
      <v-card class="mb-6" outlined>
        <v-card-title class="subtitle-1 font-weight-bold">
          <v-icon left color="amber darken-2">mdi-star</v-icon>
          Voti MVP
          <v-spacer />
          <span class="caption">{{ mvpData.total_votes }} voti totali</span>
        </v-card-title>
        <v-card-text>
          <div v-if="mvpVoteRows.length">
            <v-simple-table dense class="mb-4">
              <thead>
                <tr>
                  <th>Votante</th>
                  <th>Voto MVP</th>
                  <th class="text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in mvpVoteRows" :key="row.voterId">
                  <td>
                    <div class="d-flex align-center">
                      <v-avatar size="28" class="mr-2">
                        <v-img v-if="row.voter && row.voter.image" :src="row.voter.image" />
                        <span v-else class="caption font-weight-black">{{ initials(row.voter) }}</span>
                      </v-avatar>
                      {{ displayName(row.voter) }}
                    </div>
                  </td>
                  <td>
                    <v-select
                      :items="mvpTargetItems"
                      :value="getMvpEditValue(row)"
                      item-text="text"
                      item-value="value"
                      dense
                      outlined
                      hide-details
                      clearable
                      :disabled="!canEditStats"
                      @change="setMvpEdit(row.voterId, $event)"
                    />
                  </td>
                  <td class="text-right">
                    <v-btn
                      small
                      color="primary"
                      depressed
                      :loading="savingMvpMap[row.voterId]"
                      :disabled="!canEditStats || !hasMvpChanged(row)"
                      @click="saveMvpVote(row)"
                    >
                      Salva
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
          <div v-else class="text--secondary mb-4">Nessun partecipante registrato disponibile per il voto MVP</div>

          <div v-if="mvpData.ranking && mvpData.ranking.length">
            <div class="caption font-weight-bold mb-2">Classifica MVP</div>
            <v-list dense>
              <v-list-item v-for="(entry, i) in mvpData.ranking" :key="i">
                <v-list-item-avatar size="32">
                  <v-img v-if="entry.user && entry.user.image" :src="entry.user.image" />
                  <v-avatar v-else size="32" color="teal lighten-4">
                    <span class="caption font-weight-bold">{{ initials(entry.user) }}</span>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ displayName(entry.user) }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip small :color="i === 0 ? 'amber darken-2' : 'grey lighten-2'" :dark="i === 0">
                    {{ entry.votes }} voti
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
          <div v-else class="text--secondary">Nessun voto MVP</div>
        </v-card-text>
      </v-card>

      <!-- Stats per player -->
      <v-card class="msd-player-card" outlined>
        <v-card-title class="subtitle-1 font-weight-bold msd-section-title">
          <span>
            <v-icon left color="primary">mdi-chart-bar</v-icon>
            Statistiche giocatori
          </span>
          <v-chip small color="primary" outlined>{{ allStats.length }} giocatori</v-chip>
        </v-card-title>
        <v-card-subtitle>
          <v-alert dense outlined type="info" class="mb-0">
            Le modifiche sono consentite solo per partite terminate. Al salvataggio vengono ricalcolati statistiche utente e classifica dello sport.
          </v-alert>
        </v-card-subtitle>

        <v-card-text v-if="!allStats.length" class="text--secondary">
          Nessuna statistica inserita per questa partita
        </v-card-text>

        <v-expansion-panels v-else v-model="openPlayerPanels" multiple flat>
          <v-expansion-panel v-for="stat in allStats" :key="statKey(stat)" class="msd-player-panel">
            <v-expansion-panel-header class="msd-player-header">
              <div class="msd-player-header__inner">
                <div class="msd-player-header__main">
                  <v-avatar size="38" class="msd-player-header__avatar">
                    <v-img v-if="stat.user_id && stat.user_id.image" :src="stat.user_id.image" />
                    <span v-else class="caption font-weight-black">{{ initials(stat.user_id) }}</span>
                  </v-avatar>
                  <div class="msd-player-header__identity">
                    <strong>{{ displayName(stat.user_id) }}</strong>
                    <div class="caption text--secondary">Statistiche partita</div>
                  </div>
                </div>
                <div class="msd-player-header__meta">
                  <v-chip small :color="teamColor(teamForStat(stat))" dark>
                    {{ teamLabel(teamForStat(stat)) }}
                  </v-chip>
                  <v-chip small :color="statusColor(stat.status)" dark>{{ statusLabel(stat.status) }}</v-chip>
                  <v-chip
                    v-if="stat.match_result"
                    small
                    outlined
                    :color="resultColor(stat.match_result)"
                  >
                    {{ resultLabel(stat.match_result) }}
                  </v-chip>
                </div>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <!-- Sport-specific stats -->
              <v-row v-if="statFields(stat).length" class="mt-1 msd-stat-grid">
                <v-col
                  v-for="field in statFields(stat)"
                  :key="field.key"
                  cols="12" sm="6" md="3"
                >
                  <v-text-field
                    :label="field.label"
                    :value="getStatEditValue(stat, field.key)"
                    dense
                    outlined
                    hide-details
                    type="number"
                    :disabled="!canEditStats"
                    @input="setEdit(statKey(stat), field.key, $event)"
                  />
                </v-col>

              </v-row>
              <v-alert v-else dense outlined type="warning" class="mb-0">
                Nessuna statistica configurata per questo sport.
              </v-alert>

              <div class="msd-player-actions mt-3">
                <v-btn
                  small
                  color="primary"
                  depressed
                  :loading="savingMap[statKey(stat)]"
                  :disabled="!canEditStats || !hasStatChanges(stat)"
                  @click="saveStat(stat)"
                >
                  <v-icon left small>mdi-content-save</v-icon>
                  Salva modifiche
                </v-btn>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>
    </template>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Chiudi</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import TitlePage from '@/components/utils/TitlePage';

export default {
  name: 'MatchStatsDetail',
  components: { TitlePage },
  data() {
    return {
      loading: true,
      match: null,
      sportConfig: [],
      registeredPlayers: [],
      allStats: [],
      openPlayerPanels: [],
      scoreEdit: { final_score_a: '', final_score_b: '' },
      mvpData: { total_votes: 0, ranking: [], votes: [] },
      editMap: {},
      mvpEditMap: {},
      savingMap: {},
      savingScore: false,
      savingMvpMap: {},
      snackbar: { show: false, text: '', color: 'success' },
    };
  },
  computed: {
    matchId() {
      return this.$route.params.matchId;
    },
    canEditStats() {
      return String((this.match && this.match.status) || '').toLowerCase() === 'finished';
    },
    finalScoreA() {
      const source = this.match || {};
      if (source.final_score_a !== null && source.final_score_a !== undefined) return source.final_score_a;
      const stat = this.allStats.find(s => s.final_score_a !== null && s.final_score_a !== undefined);
      return stat ? stat.final_score_a : '-';
    },
    finalScoreB() {
      const source = this.match || {};
      if (source.final_score_b !== null && source.final_score_b !== undefined) return source.final_score_b;
      const stat = this.allStats.find(s => s.final_score_b !== null && s.final_score_b !== undefined);
      return stat ? stat.final_score_b : '-';
    },
    hasScoreChanges() {
      return String(this.scoreEdit.final_score_a) !== String(this.finalScoreA)
        || String(this.scoreEdit.final_score_b) !== String(this.finalScoreB);
    },
    canSaveScore() {
      return this.hasScoreChanges
        && this.scoreEdit.final_score_a !== ''
        && this.scoreEdit.final_score_a !== null
        && this.scoreEdit.final_score_b !== ''
        && this.scoreEdit.final_score_b !== null;
    },
    mvpTargetItems() {
      return this.registeredPlayers.map(player => ({
        text: this.displayName(player),
        value: String(player._id),
      }));
    },
    mvpVoteRows() {
      const votesByVoter = new Map((this.mvpData.votes || []).map(vote => [
        String(vote.voter_id && vote.voter_id._id ? vote.voter_id._id : vote.voter_id),
        vote,
      ]));

      return this.registeredPlayers.map(player => {
        const voterId = String(player._id);
        const vote = votesByVoter.get(voterId) || null;
        const votedFor = vote ? vote.voted_for_id : null;
        return {
          voterId,
          voter: player,
          vote,
          votedForId: votedFor ? String(votedFor._id || votedFor) : '',
        };
      });
    },
  },
  async created() {
    await this.fetchData();
  },
  methods: {
    ...mapActions({
      adminGetMatchStats: 'matchStats/adminGetMatchStats',
      adminUpdateStat: 'matchStats/adminUpdateStat',
      adminUpdatePlayerStat: 'matchStats/adminUpdatePlayerStat',
      adminUpdateMatchScore: 'matchStats/adminUpdateMatchScore',
      adminUpdateMvpVote: 'matchStats/adminUpdateMvpVote',
    }),
    async fetchData() {
      this.loading = true;
      try {
        const data = await this.adminGetMatchStats(this.matchId);
        if (data) {
          this.match = data.match || null;
          this.sportConfig = data.sport_config || [];
          this.registeredPlayers = data.registered_players || [];
          const statRows = data.submitted_stats || data.all_stats || [
            ...(data.approved_stats || []),
            ...(data.my_submission ? [data.my_submission] : []),
          ];
          this.allStats = this.buildStatsRowsForRegisteredPlayers(statRows);
          this.openPlayerPanels = this.allStats.map((_, index) => index);
          this.scoreEdit = {
            final_score_a: this.finalScoreA === '-' ? '' : this.finalScoreA,
            final_score_b: this.finalScoreB === '-' ? '' : this.finalScoreB,
          };
        } else {
          this.toast('Statistiche non disponibili per questa partita', 'error');
        }

        this.mvpData = data && data.mvp
          ? data.mvp
          : { total_votes: 0, ranking: [], votes: [] };
      } finally {
        this.loading = false;
      }
    },
    setEdit(statId, key, value) {
      if (!this.editMap[statId]) {
        this.$set(this.editMap, statId, {});
      }
      this.$set(this.editMap[statId], key, value);
    },
    buildStatsRowsForRegisteredPlayers(statRows = []) {
      const statsByUser = new Map();
      (statRows || []).forEach(stat => {
        const userId = this.userIdFromStat(stat);
        if (userId && !statsByUser.has(userId)) statsByUser.set(userId, stat);
      });

      if (this.registeredPlayers.length) {
        return this.registeredPlayers.map(player => {
          const playerId = String(player._id);
          const stat = statsByUser.get(playerId);
          if (stat) return stat;

          return {
            _id: null,
            match_id: this.matchId,
            user_id: player,
            status: 'pending',
            sport_master: this.match && this.match.sport_master,
            stats: {},
            final_score_a: this.match ? this.match.final_score_a : null,
            final_score_b: this.match ? this.match.final_score_b : null,
            match_result: null,
            submitted_by: null,
            submitted_at: null,
            auto_approve_at: null,
            confirmed_at: null,
          };
        });
      }

      const seen = new Set();
      return (statRows || []).filter(stat => {
        const key = stat._id || `user:${this.userIdFromStat(stat)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    statKey(stat) {
      return stat && stat._id ? stat._id : `user:${this.userIdFromStat(stat)}`;
    },
    userIdFromStat(stat) {
      const user = stat && stat.user_id;
      return user ? String(user._id || user) : '';
    },
    setMvpEdit(voterId, value) {
      this.$set(this.mvpEditMap, voterId, value || '');
    },
    getMvpEditValue(row) {
      if (Object.prototype.hasOwnProperty.call(this.mvpEditMap, row.voterId)) {
        return this.mvpEditMap[row.voterId];
      }
      return row.votedForId || '';
    },
    hasMvpChanged(row) {
      if (!Object.prototype.hasOwnProperty.call(this.mvpEditMap, row.voterId)) return false;
      return String(this.mvpEditMap[row.voterId] || '') !== String(row.votedForId || '');
    },
    hasStatChanges(stat) {
      const edits = this.editMap[this.statKey(stat)] || {};
      return Object.keys(edits).length > 0;
    },
    statFields(stat) {
      const fieldsByKey = new Map();
      (this.sportConfig || []).forEach(field => {
        if (field && field.key) fieldsByKey.set(field.key, { key: field.key, label: field.label || this.formatKey(field.key) });
      });
      Object.keys((stat && stat.stats) || {}).forEach(key => {
        if (!fieldsByKey.has(key)) fieldsByKey.set(key, { key, label: this.formatKey(key) });
      });
      return Array.from(fieldsByKey.values());
    },
    getStatEditValue(stat, key) {
      const edits = this.editMap[this.statKey(stat)];
      if (edits && Object.prototype.hasOwnProperty.call(edits, key)) return edits[key];
      return stat.stats && stat.stats[key] != null ? stat.stats[key] : 0;
    },
    async saveMatchScore() {
      if (!this.canEditStats) {
        this.toast('Puoi modificare il risultato solo per partite terminate', 'error');
        return;
      }
      if (!this.canSaveScore) {
        this.toast('Inserisci il punteggio di entrambe le squadre', 'error');
        return;
      }
      this.savingScore = true;
      try {
        await this.adminUpdateMatchScore({
          matchId: this.matchId,
          payload: {
            final_score_a: Number(this.scoreEdit.final_score_a),
            final_score_b: Number(this.scoreEdit.final_score_b),
          },
        });
        this.toast('Risultato aggiornato. Vittorie, pareggi, sconfitte, MVP e classifica ricalcolati.', 'success');
        await this.fetchData();
      } catch (e) {
        const message = e && e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'Errore durante il salvataggio del risultato';
        this.toast(message, 'error');
      } finally {
        this.savingScore = false;
      }
    },
    async saveMvpVote(row) {
      if (!this.canEditStats) {
        this.toast('Puoi modificare i voti MVP solo per partite terminate', 'error');
        return;
      }
      this.$set(this.savingMvpMap, row.voterId, true);
      try {
        await this.adminUpdateMvpVote({
          matchId: this.matchId,
          voterId: row.voterId,
          votedForId: this.getMvpEditValue(row),
        });
        this.$delete(this.mvpEditMap, row.voterId);
        this.toast('Voto MVP aggiornato. Classifica e statistiche utente ricalcolate.', 'success');
        await this.fetchData();
      } catch (e) {
        const message = e && e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'Errore durante il salvataggio del voto MVP';
        this.toast(message, 'error');
      } finally {
        this.$set(this.savingMvpMap, row.voterId, false);
      }
    },
    async saveStat(stat) {
      if (!this.canEditStats) {
        this.toast('Puoi modificare le statistiche solo per partite terminate', 'error');
        return;
      }
      const key = this.statKey(stat);
      this.$set(this.savingMap, key, true);
      try {
        const edits = this.editMap[key] || {};
        const newStats = { ...stat.stats };
        for (const [k, v] of Object.entries(edits)) {
          if (!k.startsWith('__')) {
            newStats[k] = Number(v);
          }
        }
        const payload = { stats: newStats };

        if (stat._id) {
          await this.adminUpdateStat({ statId: stat._id, payload });
        } else {
          await this.adminUpdatePlayerStat({
            matchId: this.matchId,
            userId: this.userIdFromStat(stat),
            payload,
          });
        }
        this.$set(this.editMap, key, null);
        this.toast('Statistica aggiornata. Totali utente e classifica ricalcolati.', 'success');
        await this.fetchData();
      } catch (e) {
        const message = e && e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'Errore durante il salvataggio';
        this.toast(message, 'error');
      } finally {
        this.$set(this.savingMap, key, false);
      }
    },
    toast(text, color = 'success') {
      this.snackbar = { show: true, text, color };
    },
    initials(user) {
      if (!user) return '?';
      return [user.name, user.surname].filter(Boolean).map(s => s[0]).join('').toUpperCase() || '?';
    },
    displayName(user) {
      if (!user) return 'Utente';
      return [user.name, user.surname].filter(Boolean).join(' ') || 'Utente';
    },
    playerForStat(stat) {
      const userId = this.userIdFromStat(stat);
      return this.registeredPlayers.find(player => String(player._id) === userId) || null;
    },
    teamForStat(stat) {
      const player = this.playerForStat(stat);
      return player && player.team ? player.team : null;
    },
    teamLabel(team) {
      if (team === 'A') return 'Squadra A';
      if (team === 'B') return 'Squadra B';
      return 'Team non assegnato';
    },
    teamColor(team) {
      if (team === 'A') return 'indigo';
      if (team === 'B') return 'deep-orange';
      return 'grey';
    },
    formatKey(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
    statusLabel(s) {
      const map = {
        pending: 'Da confermare',
        confirmed_by_organizer: 'Confermato',
        auto_approved: 'Auto approvato',
      };
      return map[s] || s || '-';
    },
    statusColor(s) {
      const map = {
        pending: 'orange',
        confirmed_by_organizer: 'green',
        auto_approved: 'teal',
      };
      return map[s] || 'grey';
    },
    resultColor(r) {
      return r === 'win' ? 'green' : r === 'draw' ? 'amber darken-2' : 'red';
    },
    resultLabel(r) {
      return r === 'win' ? 'Vittoria' : r === 'draw' ? 'Pareggio' : 'Sconfitta';
    },
    resultClass(r) {
      return r === 'win' ? 'green--text' : r === 'draw' ? 'amber--text text--darken-2' : 'red--text';
    },
  },
};
</script>

<style scoped>
.msd-score-card {
  border-color: #d7dee8;
  background: linear-gradient(135deg, #f8fafc 0%, #eef4ff 100%);
}

.msd-score {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.msd-score__board {
  display: flex;
  align-items: stretch;
  gap: 10px;
}

.msd-score__team {
  min-width: 120px;
  padding: 12px 16px;
  border-radius: 14px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
}

.msd-score__team--a {
  background: #3949ab;
}

.msd-score__team--b {
  background: #f4511e;
}

.msd-score__team span {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.86;
}

.msd-score__team strong {
  font-size: 34px;
  line-height: 1;
}

.msd-score__input {
  max-width: 88px;
  margin-top: 6px;
}

.msd-score__input >>> input {
  color: #0f172a;
  font-size: 28px;
  font-weight: 900;
  text-align: center;
}

.msd-score__input >>> .v-input__slot {
  border-radius: 10px;
  min-height: 42px !important;
}

.msd-score__save {
  align-self: center;
  min-height: 42px;
  margin-left: 6px;
}

.msd-score__separator {
  align-self: center;
  color: #64748b;
  font-size: 26px;
  font-weight: 800;
}

.msd-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.msd-player-panel {
  border: 1px solid #e6ebf2;
  border-radius: 14px !important;
  margin-bottom: 12px;
  overflow: hidden;
}

.msd-player-header {
  min-height: 76px;
  background: #fbfcfe;
}

.msd-player-header__inner {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 8px;
}

.msd-player-header__main,
.msd-player-header__meta,
.msd-player-actions {
  display: flex;
  align-items: center;
}

.msd-player-header__main {
  min-width: 220px;
}

.msd-player-header__avatar {
  margin-right: 12px;
  background: #dbeafe;
}

.msd-player-header__identity {
  line-height: 1.2;
}

.msd-player-header__meta {
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-left: auto;
}

.msd-stat-grid {
  padding-top: 6px;
}

.msd-player-actions {
  justify-content: flex-end;
}

@media (max-width: 760px) {
  .msd-score {
    align-items: flex-start;
    flex-direction: column;
  }

  .msd-score__board {
    width: 100%;
    flex-wrap: wrap;
    gap: 8px;
  }

  .msd-score__team {
    flex: 1;
    min-width: 0;
  }

  .msd-score__save {
    width: 100%;
    margin-left: 0;
  }

  .msd-player-header {
    align-items: flex-start;
  }

  .msd-player-header__inner {
    flex-direction: column;
    align-items: flex-start;
  }

  .msd-player-header__main {
    min-width: 0;
  }

  .msd-player-header__meta {
    justify-content: flex-start;
    margin-left: 0;
    margin-top: 6px;
    width: 100%;
  }
}
</style>
